<template>
  <modal
    :show.sync="showModal"
    id="giftCardActivityLogModal"
    body-classes="p-0"
    style="overflow-y: scroll"
    modal-classes="modal-dialog-centered modal-lg"
    @close="onClose"
  >
    <card
      type="secondary"
      header-classes="bg-white pb-5"
      body-classes="px-lg-5 py-lg-5"
      class="border-0 mb-0"
    >
      <div class="d-flex align-items-center mb-2">
        <badge type="default" v-if="giftCardCode">
          <code style="color: inherit">{{ giftCardCode }}</code>
        </badge>
      </div>
      <h4 class="card-title">Activity log</h4>

      <div class="mt-3" v-if="loading">
        <div class="font-icon-detail">
          <svg class="local-spinner" viewBox="0 0 50 50">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
          </svg>
          <h4 class="mt-3">Loading activity log...</h4>
        </div>
      </div>

      <div class="mt-3" v-if="!loading && activityLog.length <= 0">
        <div class="font-icon-detail">
          <span style="font-size: 3em">
            <i class="fas fa-exclamation-triangle"></i>
          </span>
          <h4 class="mt-3">No events have been recorded.</h4>
        </div>
      </div>

      <div v-if="!loading && activityLog.length > 0" class="activity-log-container">
        <b-table :items="activityLog" :fields="tableFields">
          <template v-slot:cell(event)="row">
            {{ row.item.description }}<span v-if="row.item.notes && !hasNumericValues(row.item)" class="text-lowercase"> {{ row.item.notes }}</span>
            <span v-if="row.item.new_value && row.item.old_value && isBalanceChange(row.item)">
              from {{ formatAmount(row.item.old_value) }} to {{ formatAmount(row.item.new_value) }}
            </span>
            <span v-else-if="row.item.new_value && row.item.old_value && isTime(row.item.new_value)">
              from {{ getTime(row.item.old_value) }} to {{ getTime(row.item.new_value) }}
            </span>
            <span v-else-if="row.item.new_value && row.item.old_value && isDateFormat(row.item.new_value)">
              from {{ getFormattedDate(row.item.old_value) }} to {{ getFormattedDate(row.item.new_value) }}
            </span>
            <span v-else-if="row.item.new_value && row.item.old_value">
              from {{ row.item.old_value }} to {{ row.item.new_value }}
            </span>
            <span v-else-if="row.item.new_value && !row.item.old_value">
              to {{ row.item.new_value }}
            </span>
            <div v-if="row.item.notes && hasNumericValues(row.item)" class="text-lowercase" style="font-size: 12px; opacity: 0.6;">
              {{ row.item.notes }}
            </div>
            <div v-if="row.item.order_number">
              <small>
                <span class="text-white">Transaction </span>
                <a
                  v-if="hasTransactionPermission"
                  :href="'/purchases/transactions/details?id=' + row.item.transaction_id"
                  target="_blank"
                  class="text-primary"
                >
                  #{{ row.item.order_number }}
                </a>
                <span v-else class="text-white">#{{ row.item.order_number }}</span>
              </small>
            </div>
          </template>
          <template v-slot:cell(timestamp)="row">
            {{ getLocalDate(row.item.timestamp) }}, {{ getLocalTime(row.item.timestamp) }}
          </template>
          <template v-slot:cell(performed_by)="row">
            <span v-if="!row.item.end_user_id && !row.item.company_user_id">System</span>
            <span v-if="row.item.end_user_id && !row.item.company_user_id">
              {{ row.item.end_user_name }}
            </span>
            <span v-if="!row.item.end_user_id && row.item.company_user_id">
              {{ row.item.company_user_name }}
            </span>
          </template>
        </b-table>
      </div>

      <div class="row justify-content-center mt-3">
        <div class="col text-center">
          <base-button type="secondary" @click="onClose">Close</base-button>
        </div>
      </div>
    </card>
  </modal>
</template>

<script>
import { Modal } from '@/components';
import Badge from '@/components/Badge';
import { BTable } from 'bootstrap-vue';
import moment from 'moment';
import _ from 'lodash';
import currencies from '@/assets/currencies.json';
import {formatGroupDate, formatGroupTime} from '@/plugins/dates';

export default {
  name: 'GiftCardActivityLogModal',

  components: {
    Modal,
    Badge,
    BTable
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    activityLog: {
      type: Array,
      default: () => []
    },
    giftCardCode: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    dateFormat: {
      type: String,
      default: 'MM/DD/YYYY'
    },
    timeFormat: {
      type: String,
      default: 'hh:mm A'
    },
    currencySymbol: {
      type: String,
      default: '$'
    },
    currency: {
      type: String,
      default: 'USD'
    },
    currencyFormat: {
      type: Number,
      default: 0
    },
    numberFormat: {
      type: Number,
      default: 0
    },
    hasTransactionPermission: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currencies: currencies.currencies,
      tableFields: ['event', 'performed_by', 'timestamp']
    };
  },

  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    }
  },

  methods: {
    onClose() {
      this.$emit('close');
      this.showModal = false;
    },

    hasNumericValues(item) {
      return item.old_value && item.new_value
        && !isNaN(parseFloat(item.old_value)) && !isNaN(parseFloat(item.new_value))
        && !this.isTime(item.new_value) && !this.isDateFormat(item.new_value);
    },

    isBalanceChange(item) {
      // Check if this is a balance-related event by looking at the description
      // or by checking if the values look like currency amounts
      const balanceKeywords = ['balance', 'redeemed', 'refund', 'adjustment', 'value'];
      const descriptionLower = (item.description || '').toLowerCase();
      const hasBalanceKeyword = balanceKeywords.some(keyword => descriptionLower.includes(keyword));

      // Also check if the values are numeric (could be balance values)
      const isNumericValues = !isNaN(parseFloat(item.old_value)) && !isNaN(parseFloat(item.new_value));

      return hasBalanceKeyword && isNumericValues;
    },

    formatAmount(value) {
      if (value == null) {
        return '';
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return value;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currencySymbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.numberFormat === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = numValue.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = numValue.toFixed(2);
      }

      if (this.currencyFormat === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;
        } else {
          return symbol + formattedAmount;
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;
      }
    },

    isTime(str) {
      return /^\d{2}:\d{2}(:\d{2})?$/.test(str);
    },

    isDateFormat(str) {
      return moment(str, 'YYYY-MM-DD', true).isValid();
    },

    getTime(time) {
      if (!time) return '';
      const t = time.includes('T') ? time.split('T')[1] : time;
      const [hours, minutes] = t.split(':');
      const h = parseInt(hours);

      if (this.timeFormat === 'HH:mm') {
        return `${hours}:${minutes}`;
      }

      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    },

    getFormattedDate(d) {
      if (!d) return '';
      let date = '';
      if (d.length > 10) {
        const dd = d.split('T')[0];
        date = dd.split('-');
      } else {
        date = d.split('-');
      }

      if (this.dateFormat === 'MM/DD/YYYY') {
        return ('0' + date[1]).slice(-2) + '/' + ('0' + date[2]).slice(-2) + '/' + date[0];
      } else if (this.dateFormat === 'DD/MM/YYYY') {
        return ('0' + date[2]).slice(-2) + '/' + ('0' + date[1]).slice(-2) + '/' + date[0];
      } else if (this.dateFormat === 'YYYY/MM/DD') {
        return date[0] + '/' + ('0' + date[1]).slice(-2) + '/' + ('0' + date[2]).slice(-2);
      }
      return ('0' + date[1]).slice(-2) + '/' + ('0' + date[2]).slice(-2) + '/' + date[0];
    },

    getDayOfWeek(d) {
      if (!d) return '';
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dt = new Date();
      let date = '';
      if (d.length > 10) {
        const dd = d.split('T')[0];
        date = dd.split('-');
      } else {
        date = d.split('-');
      }
      dt.setFullYear(date[0]);
      dt.setMonth(parseInt(date[1] - 1), date[2]);
      return days[dt.getDay()] + ', ';
    },

    getLocalDate(d) {
      return formatGroupDate(d);
    },

    getLocalTime(d) {
      return formatGroupTime(d);
    }
  }
};
</script>

<style scoped>
.font-icon-detail {
  text-align: center;
  padding: 45px 0 30px;
  border: 1px solid #e3e3e3;
  border-radius: 0.4285rem;
  margin: 15px 0;
  min-height: 200px;
}

.activity-log-container {
  max-height: 400px;
  overflow-y: auto;
}

.local-spinner {
  animation: rotate 1s linear infinite;
  width: 60px;
  height: 60px;
}

.local-spinner .path {
  stroke: #c2c1c1;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 120, 150;
    stroke-dashoffset: -45;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -135;
  }
}

::v-deep .table th,
::v-deep .table td {
  text-align: left;
}

::v-deep .table th:nth-child(2),
::v-deep .table td:nth-child(2),
::v-deep .table th:nth-child(3),
::v-deep .table td:nth-child(3) {
  white-space: nowrap;
}

/* Dark mode support for Black Dashboard theme */
::v-deep .card.card-secondary {
  background-color: #22233a;
}

::v-deep .card.card-secondary .card-title {
  color: #ffffff;
}

::v-deep .card.card-secondary .table {
  color: #ffffff;
}

::v-deep .card.card-secondary .table th {
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}

::v-deep .card.card-secondary .table td {
  border-color: rgba(255, 255, 255, 0.1);
}

::v-deep .card.card-secondary .font-icon-detail {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
}

::v-deep .card.card-secondary .font-icon-detail h4 {
  color: rgba(255, 255, 255, 0.7);
}

::v-deep .card.card-secondary .font-icon-detail i {
  color: rgba(255, 255, 255, 0.5);
}
</style>

<style>
/* Light mode overrides */
.white-content #giftCardActivityLogModal .card.card-secondary {
  background-color: #ffffff;
}

.white-content #giftCardActivityLogModal .card-title {
  color: rgba(0, 0, 0, 0.85);
}

.white-content #giftCardActivityLogModal .table {
  color: rgba(0, 0, 0, 0.85);
}

.white-content #giftCardActivityLogModal .table th {
  color: rgba(0, 0, 0, 0.5);
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content #giftCardActivityLogModal .table td {
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content #giftCardActivityLogModal .text-white {
  color: rgba(0, 0, 0, 0.85) !important;
}

.white-content #giftCardActivityLogModal .font-icon-detail {
  border-color: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.5);
}

.white-content #giftCardActivityLogModal .font-icon-detail h4 {
  color: rgba(0, 0, 0, 0.5);
}

.white-content #giftCardActivityLogModal .font-icon-detail i {
  color: rgba(0, 0, 0, 0.35);
}

.white-content #giftCardActivityLogModal .badge-default {
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.8);
}

.white-content #giftCardActivityLogModal .badge-default code {
  color: rgba(0, 0, 0, 0.8);
}
</style>
